<script setup>
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import FloatLabel from 'primevue/floatlabel'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import DatePicker from 'primevue/datepicker';
import { useToast } from 'primevue/usetoast'
import { useFacilityStore } from '@/stores/facility.js'
import AutoComplete from 'primevue/autocomplete'
import FacilityParticipantsEditor from '@/components/facility/FacilityParticipantsEditor.vue'


const props = defineProps({
  visible: Boolean,
  facility: Object,
  presetCustomerId: { type: [Number, String], required: false }

})
const emit = defineEmits(['update:visible', 'close', 'save'])

const toast = useToast()
const facilityStore = useFacilityStore()

const emptyForm = {
  name: '',
  short_name: '',
  address: '',
  phone: '',
  email: '',
  term_start: null,
  term_end: null,
  rep_post: '',
  rep_name: '',
  rep_phone: '',
  rep_note: '',
  comments: ''
}

const form = ref({ ...emptyForm })
const participants = ref([]) // [{ customer_id, role }]
const addressOptions = ref([])
let addressTimer = null


watch(
    [() => props.visible, () => props.facility, () => props.presetCustomerId],
    ([vis, facility]) => {
      if (vis) {
        if (facility) {
          const copy = JSON.parse(JSON.stringify(facility))
          form.value = {
            name: copy?.name || copy?.item_name || '',
            short_name: copy?.short_name || copy?.item_short || '',
            address: copy?.address || copy?.item_address || '',
            phone: copy?.phone || copy?.tel || '',
            email: copy?.email || '',
            term_start: copy?.term_start ? new Date(copy.term_start) : null,
            term_end: copy?.term_end ? new Date(copy.term_end) : null,
            rep_post: copy?.rep_post || '',
            rep_name: copy?.rep_name || '',
            rep_phone: copy?.rep_phone || '',
            rep_note: copy?.rep_note || '',
            comments: copy?.comments || ''
          }
          // participants загрузит сам редактор по facilityId
        } else {
          form.value = { ...emptyForm }
          // При создании — если пришёл пресет клиента, установим "Заказчик" по умолчанию в редакторе (через prop)
          participants.value = []
        }
      }
    },
    { immediate: true }
)


function toIsoDate(val) {
  if (!val) return null
  if (typeof val === 'string') return val
  // Date -> 'YYYY-MM-DD'
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}


function close() {
  emit('update:visible', false)
  emit('close')
}

async function submit() {
  const payload = {
    name: form.value.name?.trim() || '',
    short_name: form.value.short_name?.trim() || '',
    address: form.value.address?.trim() || '',
    phone: form.value.phone?.trim() || '',
    email: form.value.email?.trim() || '',
    term_start: toIsoDate(form.value.term_start),
    term_end: toIsoDate(form.value.term_end),
    rep_post: form.value.rep_post?.trim() || '',
    rep_name: form.value.rep_name?.trim() || '',
    rep_phone: form.value.rep_phone?.trim() || '',
    rep_note: form.value.rep_note?.trim() || '',
    comments: form.value.comments?.trim() || ''
  }

  try {
    if (props.facility?.id) {
      // Редактирование: сохраняем объект + связи
      await facilityStore.updateFacilityWithParticipants(props.facility.id, payload, participants.value, 'put')
      toast.add({ severity: 'success', summary: 'Объект обновлён', detail: 'Данные сохранены', life: 2500 })
      close()
    } else {
      // Создание: создаём объект и устанавливаем связи (если presetCustomerId, редактор уже отдаст CUSTOMER)
      const created = await facilityStore.createFacilityWithParticipants(payload, participants.value)
      if (created?.id) {
        toast.add({ severity: 'success', summary: 'Объект создан', detail: 'Данные сохранены', life: 2500 })
      }
      emit('save', created || payload)
      // Родитель закроет форму
    }
  } catch (e) {
    console.error('FacilityForm submit error', e)
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось сохранить объект', life: 4000 })
  }
}




// Адрес: автодополнение по бэкенду (dadata proxy)
// e.query — текущая строка, берём топ-5
async function completeAddress(e) {
  const query = String(e?.query || '').trim()
  if (addressTimer) clearTimeout(addressTimer)
  if (!query) {
    addressOptions.value = []
    return
  }
  addressTimer = setTimeout(async () => {
    try {
      // ленивый импорт API, чтобы не плодить связей
      const { default: facilityApi } = await import('@/api/facilityApi.js')
      const resp = await facilityApi.addressSuggest({ query, count: 5 })
      // Универсальный разбор возможных структур
      const list = resp?.suggestions || resp?.results || resp?.data || resp || []
      addressOptions.value = (Array.isArray(list) ? list : []).map((s) =>
          s?.value || s?.unrestricted_value || s?.address?.value || s?.raw || String(s)
      ).filter(Boolean)
    } catch (err) {
      console.error('addressSuggest error', err)
      addressOptions.value = []
    }
  }, 250) // debounce ~250ms
}

function onAddressSelect(val) {
  form.value.address = typeof val === 'string' ? val : (val?.value || '')
}

function onUpdateVisible(val) {
  emit('update:visible', val)
  if (!val) emit('close')
}

const dialogHeader = computed(() =>
    props.facility ? 'Редактировать данные объекта' : 'Добавить новый объект'
)

</script>
<template>
  <Dialog
      :visible="visible"
      @update:visible="onUpdateVisible"
      modal
      :header="dialogHeader"
      :style="{ width: '800px' }"
      :closable="true"
      :position="'top'"
  >
    <form @submit.prevent="submit" class="flex flex-col gap-6">
      <Accordion :value="['0']" multiple>
        <AccordionPanel value="0">
          <AccordionHeader>Основные данные</AccordionHeader>
          <AccordionContent>
            <div class="flex flex-col gap-6">
              <FloatLabel variant="on">
                <InputText id="name" v-model="form.name" required class="w-full" autocomplete="off" />
                <label for="name">Полное наименование</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="short_name" v-model="form.short_name" required class="w-full" autocomplete="off" />
                <label for="short_name">Краткое наименование</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <AutoComplete
                    inputId="address"
                    v-model="form.address"
                    :suggestions="addressOptions"
                    @complete="completeAddress"
                    @item-select="(e)=>onAddressSelect(e.value)"
                    forceSelection="false"
                    class="w-full"
                    :pt="{ input: { class: 'w-full' } }"
                    placeholder=""
                />

                <label for="address">Адрес</label>
              </FloatLabel>
              <FacilityParticipantsEditor
                  :facility-id="props.facility?.id"
                  :preset-customer-id="props.presetCustomerId"
                  v-model="participants"
              />

              <div class="flex gap-4">
                <FloatLabel variant="on" class="w-full">
                  <InputText id="phone" v-model="form.phone" class="w-full" autocomplete="off" />
                  <label for="phone">Телефон</label>
                </FloatLabel>
                <FloatLabel variant="on" class="w-full">
                  <InputText id="email" v-model="form.email" type="email" class="w-full" autocomplete="off" />
                  <label for="email">Email</label>
                </FloatLabel>
              </div>

              <div class="flex gap-4">
                <FloatLabel variant="on" class="w-full">
                  <DatePicker v-model="form.term_start" inputId="term_start" showIcon iconDisplay="input" :pt="{ input: { class: 'w-full' } }" />
                  <label for="term_start">Начало работ</label>
                </FloatLabel>
                <FloatLabel variant="on" class="w-full">
                  <DatePicker v-model="form.term_end" inputId="term_end" showIcon iconDisplay="input" :pt="{ input: { class: 'w-full' } }" />
                  <label for="term_end">Окончание работ</label>
                </FloatLabel>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>

        <AccordionPanel value="1">
          <AccordionHeader>Представитель</AccordionHeader>
          <AccordionContent>
            <div class="flex flex-col gap-6">
              <FloatLabel variant="on">
                <InputText id="rep_name" v-model="form.rep_name" class="w-full" autocomplete="off" />
                <label for="rep_name">ФИО</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="rep_post" v-model="form.rep_post" class="w-full" autocomplete="off" />
                <label for="rep_post">Должность</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="rep_phone" v-model="form.rep_phone" class="w-full" autocomplete="off" />
                <label for="rep_phone">Телефон</label>
              </FloatLabel>
              <FloatLabel variant="on">
                <InputText id="rep_note" v-model="form.rep_note" class="w-full" autocomplete="off" />
                <label for="rep_note">Примечание</label>
              </FloatLabel>
            </div>
          </AccordionContent>
        </AccordionPanel>

        <AccordionPanel value="2">
          <AccordionHeader>Дополнительно</AccordionHeader>
          <AccordionContent>
            <FloatLabel variant="on">
              <InputText id="comments" v-model="form.comments" class="w-full" autocomplete="off" />
              <label for="comments">Комментарии</label>
            </FloatLabel>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>

      <div class="flex justify-center gap-4 pt-2">
        <Button :label="props.facility ? 'Сохранить' : 'Добавить'" type="submit" severity="info" rounded />
        <Button label="Отмена" type="button" severity="secondary" rounded variant="outlined" @click="close" />
      </div>
    </form>
  </Dialog>
</template>


<style scoped >
:deep(.p-autocomplete) {
  display: flex;
  width: 100%;
}
:deep(.p-autocomplete .p-autocomplete-input) {
  width: 100%;
}
</style>